import * as Calendar from 'expo-calendar';
import type {
  CalendarEvent,
  CreateCalendarEventInput,
  UpdateCalendarEventInput,
} from '@/types/events';
import { CalendarEventMapper } from './CalendarEventMapper';
import { RecurrenceRuleMapper } from './RecurrenceRuleMapper';
import { canAccessDeviceCalendar } from './platform';

export type PermissionStatus = 'granted' | 'denied' | 'undetermined';

export type DeviceCalendar = {
  id: string;
  title: string;
  color: string;
  isPrimary: boolean;
  allowsModifications: boolean;
  source: {
    name: string;
    type: string;
  };
  ownerAccount?: string; // Android only - actual account email that owns this calendar
  isVisible?: boolean;   // Android only - true if shown in native calendar app
  isSynced?: boolean;    // Android only - true if syncing events
};

/**
 * DeviceCalendarService wraps expo-calendar to provide
 * calendar access via CalendarContract (Android) and EventKit (iOS)
 */
export class DeviceCalendarService {
  /**
   * Request calendar permissions
   */
  async requestPermissions(): Promise<{ status: PermissionStatus }> {
    if (!canAccessDeviceCalendar()) {
      return { status: 'denied' };
    }

    try {
      const { status } = await Calendar.requestCalendarPermissionsAsync();

      if (status === 'granted') {
        return { status: 'granted' };
      } else if (status === 'denied') {
        return { status: 'denied' };
      } else {
        return { status: 'undetermined' };
      }
    } catch (error) {
      console.error('[DeviceCalendarService] Error requesting permissions:', error);
      return { status: 'denied' };
    }
  }

  /**
   * Check current permission status
   */
  async checkPermissions(): Promise<{ status: PermissionStatus }> {
    if (!canAccessDeviceCalendar()) {
      return { status: 'denied' };
    }

    try {
      const { status } = await Calendar.getCalendarPermissionsAsync();

      if (status === 'granted') {
        return { status: 'granted' };
      } else if (status === 'denied') {
        return { status: 'denied' };
      } else {
        return { status: 'undetermined' };
      }
    } catch (error) {
      console.error('[DeviceCalendarService] Error checking permissions:', error);
      return { status: 'undetermined' };
    }
  }

  /**
   * Get all device calendars
   */
  async getDeviceCalendars(): Promise<DeviceCalendar[]> {
    if (!canAccessDeviceCalendar()) {
      return [];
    }

    try {
      const calendars = await Calendar.getCalendarsAsync(Calendar.EntityTypes.EVENT);

      const mappedCalendars = calendars.map(cal => ({
        id: cal.id,
        title: cal.title,
        color: cal.color ?? '#007AFF',
        isPrimary: cal.isPrimary ?? false,
        allowsModifications: cal.allowsModifications ?? false,
        source: {
          name: cal.source?.name ?? 'Unknown',
          type: cal.source?.type ?? 'unknown',
        },
        ownerAccount: cal.ownerAccount ?? undefined,
        isVisible: cal.isVisible ?? true,   // Android: false when hidden in native app
        isSynced: cal.isSynced ?? true,     // Android: false when not syncing
      }));

      return mappedCalendars;
    } catch (error) {
      console.error('[DeviceCalendarService] Error fetching calendars:', error);
      return [];
    }
  }

  /**
   * Get primary device calendar
   */
  async getPrimaryCalendar(): Promise<DeviceCalendar | null> {
    const calendars = await this.getDeviceCalendars();

    // Find primary calendar
    const primary = calendars.find(cal => cal.isPrimary);
    if (primary) return primary;

    // Fallback to first writable calendar
    const writable = calendars.find(cal => cal.allowsModifications);
    if (writable) return writable;

    // Fallback to first calendar
    return calendars[0] ?? null;
  }

  /**
   * List events from device calendars
   */
  async listEvents(
    calendarIds: string[],
    startDate?: Date,
    endDate?: Date,
  ): Promise<CalendarEvent[]> {
    if (!canAccessDeviceCalendar() || calendarIds.length === 0) {
      return [];
    }

    try {
      // Default date range: 1 month before to 3 months after
      const now = new Date();
      const defaultStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const defaultEnd = new Date(now.getFullYear(), now.getMonth() + 3, 0);

      const events = await Calendar.getEventsAsync(
        calendarIds,
        startDate ?? defaultStart,
        endDate ?? defaultEnd,
      );

      // Log calendar details in dev mode for debugging
      if (__DEV__ && events.length > 0) {
        console.log(`[DeviceCalendarService] Loaded ${events.length} events from calendars:`, calendarIds);
      }

      return events.flatMap(event => {
        try {
          return [CalendarEventMapper.mapExpoEventToCalendarEvent(event)];
        } catch (mapError) {
          console.warn(`[DeviceCalendarService] Skipping event ${event.id}: mapping failed`, mapError);
          return [];
        }
      });
    } catch (error) {
      console.error('[DeviceCalendarService] Error listing events:', error);
      return [];
    }
  }

  /**
   * Get a specific event by ID
   */
  async getEvent(eventId: string): Promise<CalendarEvent | null> {
    if (!canAccessDeviceCalendar()) {
      return null;
    }

    try {
      const event = await Calendar.getEventAsync(eventId);
      if (!event) return null;

      return CalendarEventMapper.mapExpoEventToCalendarEvent(event);
    } catch (error) {
      console.error('[DeviceCalendarService] Error getting event:', error);
      return null;
    }
  }

  /**
   * Create a new event in device calendar
   */
  async createEvent(
    calendarId: string,
    input: CreateCalendarEventInput,
  ): Promise<CalendarEvent> {
    if (!canAccessDeviceCalendar()) {
      throw new Error('Device calendar access not available on this platform');
    }

    try {
      // Map input to expo-calendar format
      const expoEvent = CalendarEventMapper.mapCalendarEventToExpoEvent(input, calendarId);

      // Extract attendees before creating event
      const attendeesToCreate = (expoEvent as { attendees?: Calendar.Attendee[] }).attendees ?? [];

      // Create event WITHOUT attendees (they'll be added separately)
      // The attendees parameter is ignored by expo-calendar on Android anyway
      const eventId = await Calendar.createEventAsync(calendarId, {
        ...expoEvent,
        attendees: undefined,  // Don't pass attendees here - they're ignored
      } as unknown as Omit<Partial<Calendar.Event>, 'id' | 'organizer'>);

      // CRITICAL: Create each attendee explicitly via Calendar.createAttendeeAsync()
      // This is required because expo-calendar doesn't process the attendees array
      if (attendeesToCreate.length > 0) {
        await this.addAttendeesToEvent(eventId, attendeesToCreate);
      }

      // WORKAROUND: expo-calendar's getEventAsync() doesn't return attendees immediately
      // after createAttendeeAsync() calls, even though they ARE created in CalendarContract.
      // Instead of fetching the event (which returns undefined attendees), construct the
      // CalendarEvent from input data. Attendees will appear when event is loaded later.
      // Construct CalendarEvent from input data
      return {
        id: eventId,
        title: input.title,
        startDate: input.startDate,
        startTime: input.startTime ?? undefined,
        endDate: input.endDate ?? input.startDate,
        endTime: input.endTime ?? undefined,
        allDay: input.allDay ?? false,
        description: input.description ?? null,
        location: input.location ?? null,
        transparency: input.transparency ?? 'opaque',
        visibility: input.visibility ?? 'default',
        recurrence: input.recurrence ?? undefined,
        deviceEventId: eventId,
        deviceCalendarId: calendarId,
        isFromDevice: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        extendedProperties: {
          participants: input.extendedProperties?.participants ?? [],
          reminders: input.extendedProperties?.reminders ?? [],
        },
      } as CalendarEvent;
    } catch (error) {
      console.error('[DeviceCalendarService] Error creating event:', error);
      throw new Error(
        `Failed to create calendar event: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }

  /**
   * Add attendees to an existing event
   * MUST be called after event creation
   * 
   * expo-calendar requires explicit createAttendeeAsync() calls for each attendee.
   * The attendees array passed to createEventAsync() is ignored on Android.
   */
  private async addAttendeesToEvent(
    eventId: string,
    attendees: Calendar.Attendee[]
  ): Promise<void> {
    for (const attendee of attendees) {
      try {
        await Calendar.createAttendeeAsync(eventId, {
          email: attendee.email,
          name: attendee.name,
          role: attendee.role,
          status: Calendar.AttendeeStatus.INVITED,  // Mark as invited, not NONE
          type: attendee.type,
        });
      } catch (error) {
        console.error(`[DeviceCalendarService] Failed to create attendee ${attendee.email}:`, error);
        // Continue with other attendees even if one fails
      }
    }
  }

  /**
   * Update an existing event
   */
  async updateEvent(
    eventId: string,
    input: UpdateCalendarEventInput,
    recurringOptions?: { futureEvents: boolean; instanceStartDate?: Date },
  ): Promise<CalendarEvent> {
    if (!canAccessDeviceCalendar()) {
      throw new Error('Device calendar access not available on this platform');
    }

    try {
      // Get current event to merge with updates
      const currentEvent = await Calendar.getEventAsync(eventId);
      if (!currentEvent) {
        throw new Error('Event not found');
      }

      // Merge updates with current event
      const updates: Partial<Calendar.Event> = {};

      if (input.title !== undefined) {
        updates.title = input.title;
      }

      if (input.description !== undefined) {
        // Use empty string to clear notes (undefined means "don't change" in expo-calendar)
        updates.notes = input.description ?? '';
      }

      if (input.location !== undefined) {
        updates.location = input.location ?? undefined;
      }

      // Handle allDay property change
      if ((input as any).allDay !== undefined) {
        updates.allDay = (input as any).allDay;
      }

      // Detect recurring master so we can avoid the DTEND/DURATION conflict.
      // Android CalendarContract stores recurring masters with DURATION, not DTEND.
      // Writing DTEND to such a row throws IllegalArgumentException natively, which
      // escapes the JS try/catch under New Architecture and crashes the app.
      const isRecurringMaster = !!currentEvent.recurrenceRule?.frequency;

      if (input.startDate !== undefined || input.startTime !== undefined || (input as any).allDay !== undefined) {
        const dateStr = input.startDate ?? CalendarEventMapper['extractDate'](currentEvent.startDate);
        const wasAllDay = currentEvent.allDay === true;
        const isAllDay = (input as any).allDay ?? currentEvent.allDay;
        const timeStr = isAllDay
          ? null
          : (input.startTime !== undefined && input.startTime !== null
              ? input.startTime
              : wasAllDay
                ? '09:00'
                : CalendarEventMapper['extractTime'](currentEvent.startDate));
        const candidate = this.createDateTime(dateStr, timeStr);
        const currentMs = new Date(currentEvent.startDate).getTime();
        if (candidate.getTime() !== currentMs) {
          updates.startDate = candidate;
        }
      }

      if (input.endDate !== undefined || input.endTime !== undefined || (input as any).allDay !== undefined) {
        const dateStr = input.endDate ?? CalendarEventMapper['extractDate'](currentEvent.endDate);
        const wasAllDay = currentEvent.allDay === true;
        const isAllDay = (input as any).allDay ?? currentEvent.allDay;
        const timeStr = isAllDay
          ? null
          : (input.endTime !== undefined && input.endTime !== null
              ? input.endTime
              : wasAllDay
                ? '10:00'
                : CalendarEventMapper['extractTime'](currentEvent.endDate));
        const candidate = this.createDateTime(dateStr, timeStr);
        const currentMs = new Date(currentEvent.endDate).getTime();
        if (candidate.getTime() !== currentMs) {
          updates.endDate = candidate;
        }
      }

      if (input.transparency !== undefined) {
        updates.availability = input.transparency === 'transparent'
          ? Calendar.Availability.FREE
          : Calendar.Availability.BUSY;
      }

      if (input.visibility !== undefined) {
        updates.accessLevel = this.mapVisibilityToAccessLevel(input.visibility);
      }

      // Handle attendees update - REQUIRES explicit creation/deletion
      // expo-calendar doesn't process the attendees property update on Android
      if (input.extendedProperties?.participants !== undefined) {
        const newAttendees = input.extendedProperties.participants
          .filter(p => p.role === 'invited' || p.role === 'organiser')
          .map(p => ({
            email: p.email!,
            name: p.name ?? '',
            role: p.role === 'organiser'
              ? Calendar.AttendeeRole.ATTENDEE
              : Calendar.AttendeeRole.OPTIONAL,
            status: Calendar.AttendeeStatus.INVITED,  // Changed from NONE
            type: Calendar.AttendeeType.PERSON,
          }))
          .filter(a => a.email);

        // Get current attendees from native API (reliable, unlike currentEvent.attendees)
        const currentAttendees = await Calendar.getAttendeesForEventAsync(eventId);

        // Compute diff
        const currentEmails = new Set(currentAttendees.map(a => a.email?.toLowerCase()));
        const newEmails = new Set(newAttendees.map(a => a.email.toLowerCase()));

        // Delete removed attendees
        const toDelete = currentAttendees.filter(a =>
          a.email && !newEmails.has(a.email.toLowerCase())
        );
        for (const attendee of toDelete) {
          if (attendee.id) {
            try {
              await Calendar.deleteAttendeeAsync(attendee.id);
            } catch (error) {
              console.error(`[DeviceCalendarService] Failed to delete attendee ${attendee.email}:`, error);
            }
          }
        }

        // Add new attendees
        const toAdd = newAttendees.filter(a =>
          !currentEmails.has(a.email.toLowerCase())
        );
        if (toAdd.length > 0) {
          await this.addAttendeesToEvent(eventId, toAdd);
        }
      }

      // Handle alarms/reminders update - explicitly replace defaults
      if (input.extendedProperties?.reminders !== undefined) {
        const reminders = input.extendedProperties.reminders;
        if (reminders.length > 0) {
          const startSource = updates.startDate ?? currentEvent.startDate;
          const eventStartDate = startSource instanceof Date ? startSource : new Date(startSource);
          const alarms = reminders.map(reminder => {
            const reminderDateTime = this.createDateTime(reminder.date, reminder.time);
            const relativeOffset = Math.round((reminderDateTime.getTime() - eventStartDate.getTime()) / 60000);
            return { relativeOffset };
          });
          updates.alarms = alarms;
        } else {
          // Empty array = clear all alarms (no defaults)
          updates.alarms = [];
        }
      }

      // Handle recurrence rule update
      if (input.recurrence !== undefined) {
        updates.recurrenceRule = input.recurrence
          ? RecurrenceRuleMapper.mapToExpo(input.recurrence)
          : undefined;
      }

      // Recurring masters on Android: CalendarContract stores DURATION, not DTEND.
      // Writing DTEND to a master row throws IllegalArgumentException; passing
      // recurrenceRule alongside without complete DTSTART/DTEND throws NPE in
      // expo-calendar's native conversion code. Both crash the app under New
      // Architecture. When the user isn't explicitly editing recurrence, drop
      // any DTSTART/DTEND writes so we don't trip either path. Time edits on
      // recurring masters require a dedicated path (TODO).
      if (isRecurringMaster && input.recurrence === undefined) {
        delete updates.startDate;
        delete updates.endDate;
      }

      await Calendar.updateEventAsync(eventId, updates, recurringOptions);

      // OPTIMISTIC: Return event constructed from input instead of refetching
      // This ensures attendee/participant changes appear immediately in UI
      // while CalendarContract syncs in background (matches createEvent behavior)
      return {
        id: eventId,
        title: updates.title ?? currentEvent.title,
        startDate: CalendarEventMapper['extractDate'](updates.startDate ?? currentEvent.startDate),
        startTime: updates.allDay ? null : CalendarEventMapper['extractTime'](updates.startDate ?? currentEvent.startDate),
        endDate: CalendarEventMapper['extractDate'](updates.endDate ?? currentEvent.endDate),
        endTime: updates.allDay ? null : CalendarEventMapper['extractTime'](updates.endDate ?? currentEvent.endDate),
        allDay: updates.allDay ?? currentEvent.allDay ?? false,
        description: updates.notes ?? currentEvent.notes ?? null,
        location: updates.location ?? currentEvent.location ?? null,
        transparency: input.transparency ?? 'opaque',
        visibility: input.visibility ?? 'default',
        recurrence: input.recurrence ?? null,
        deviceEventId: eventId,
        deviceCalendarId: currentEvent.calendarId,
        isFromDevice: true,
        createdAt: this.toIsoString(currentEvent.creationDate),
        updatedAt: new Date().toISOString(),
        organizer: currentEvent.organizer ? {
          email: (currentEvent.organizer as unknown as { email?: string }).email ?? currentEvent.organizerEmail ?? undefined,
          name: currentEvent.organizer.name ?? null,
        } : (currentEvent.organizerEmail ? {
          email: currentEvent.organizerEmail,
          name: null,
        } : null),
        extendedProperties: {
          participants: input.extendedProperties?.participants ?? [],
          reminders: input.extendedProperties?.reminders ?? [],
        },
      } as CalendarEvent;
    } catch (error) {
      console.error('[DeviceCalendarService] Error updating event:', error);
      throw new Error(
        `Failed to update calendar event: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }

  /**
   * Delete an event
   */
  async deleteEvent(
    eventId: string,
    recurringOptions?: { futureEvents: boolean; instanceStartDate?: Date },
  ): Promise<void> {
    if (!canAccessDeviceCalendar()) {
      throw new Error('Device calendar access not available on this platform');
    }

    try {
      await Calendar.deleteEventAsync(eventId, recurringOptions);
    } catch (error) {
      console.error('[DeviceCalendarService] Error deleting event:', error);
      throw new Error(
        `Failed to delete calendar event: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }

  /**
   * Helper: Create Date from date and time strings
   */
  private createDateTime(dateStr: string, timeStr?: string | null): Date {
    const [year, month, day] = dateStr.split('-').map(Number);

    if (!timeStr) {
      // All-day event - create date at local midnight
      return new Date(year, month - 1, day, 0, 0, 0, 0);
    }

    // Timed event - parse date and time in local timezone
    const [hours, minutes] = timeStr.split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes, 0, 0);
  }

  /**
   * Helper: Convert string|Date to ISO string with safe fallback
   */
  private toIsoString(value: string | Date | null | undefined): string {
    if (!value) return new Date().toISOString();
    return value instanceof Date ? value.toISOString() : new Date(value).toISOString();
  }

  /**
   * Helper: Map visibility to event access level
   */
  private mapVisibilityToAccessLevel(visibility: string): Calendar.EventAccessLevel {
    switch (visibility) {
      case 'public':
        return Calendar.EventAccessLevel.PUBLIC;
      case 'private':
        return Calendar.EventAccessLevel.PRIVATE;
      case 'confidential':
        return Calendar.EventAccessLevel.CONFIDENTIAL;
      default:
        return Calendar.EventAccessLevel.DEFAULT;
    }
  }
}

// Export singleton instance
export const deviceCalendarService = new DeviceCalendarService();
